import React from "react";
import Container from "@/components/marketing/Container";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import MarqueeLogo from "@/components/marketing/MarqueeLogo";

const BASE = "https://media.base44.com/images/public/6a460432963500ce22897197/";

const FEATURED_LOGOS = [
  { name: "Reuters", src: BASE + "54195193d_Reuters_Logo_0.svg", scale: 0.9 },
  { name: "Yahoo Finance", src: BASE + "289cccfa2_Yahoo_Logo_0.svg", scale: 0.9 },
  { name: "Gulf News", src: BASE + "23eb8ad36_Gulf_News_idd14Ts0hh_1.svg", scale: 1.1 },
  { name: "CNBC", src: BASE + "bb006c585_CNBC_Logo_0.svg", scale: 0.92 },
  { name: "MarketWatch", src: BASE + "88aab5914_MarketWatch_Logo_0.svg", scale: 1.05 },
];

export default function HeroSection() {
  const { isAuthenticated } = useAuth();
  const ctaTo = isAuthenticated ? "/dashboard/new-order" : "/register";

  return (
    <header className="relative pt-20 lg:pt-24 pb-11 text-center">
      <Container>
        {/* Eyebrow */}
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6d28d9] mb-5">
          Multi-Channel PR Distribution &middot; Global
        </div>

        {/* H1 with gradient text */}
        <h1 className="font-heading text-[clamp(44px,6vw,74px)] font-[650] tracking-[-0.035em] leading-[1.03] pb-[0.12em] mb-[calc(1.25rem-0.12em)] text-heading-gradient">
          Get your story<br />everywhere that matters.
        </h1>

        {/* Subtitle */}
        <p className="text-[17px] leading-[1.55] text-[#6c6c74] max-w-[520px] mx-auto mb-8">
          One platform for earned coverage, newswires, and guaranteed placements. You choose the channels, no agencies, no lock-ins.
        </p>

        {/* CTAs */}
        <div className="flex items-center justify-center gap-3 mb-11">
          <Link to={ctaTo} data-track-label="get_started" data-track-section="hero"
            className="inline-block text-[14px] font-semibold px-6 py-[13px] rounded-[14px] bg-brand-gradient text-white whitespace-nowrap transition-opacity hover:opacity-90"
            style={{ boxShadow: "0 8px 20px rgba(124,58,237,0.35)" }}>
            Start Your Distribution
          </Link>
          <Link to="/how-it-works"
            className="inline-block text-[14px] font-semibold px-6 py-[13px] rounded-[14px] bg-dark-pill text-white whitespace-nowrap transition-colors hover:bg-[#3c3c42]">
            See How It Works
          </Link>
        </div>

        {/* Results card */}
        <div className="max-w-[680px] mx-auto">
          <div className="glass-card p-6 lg:p-7 text-left" style={{ borderRadius: 28 }}>
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Left — stat */}
              <div className="sm:border-r sm:border-[rgba(28,28,32,0.06)] sm:pr-6">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6d28d9] mb-2.5">Results</div>
                <div className="text-[15px] font-[650] mb-1 whitespace-nowrap">One campaign, real coverage</div>
                <div className="text-[13px] text-[#8e8e96] mb-5 whitespace-nowrap">What a single Digital24 distribution delivered.</div>
                <div className="text-[56px] font-bold tracking-[-0.03em] leading-none text-brand-gradient whitespace-nowrap">986M</div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8e8e96] mt-1.5">Combined Audience</div>
                <div className="flex gap-6 mt-4.5">
                  <span className="text-[13.5px] text-[#55555c] whitespace-nowrap"><strong className="font-bold text-[#1c1c20]">33</strong> placements</span>
                  <span className="text-[13.5px] text-[#55555c] whitespace-nowrap"><strong className="font-bold text-[#1c1c20]">995K</strong> est. views</span>
                </div>
              </div>

              {/* Right — featured outlets */}
              <div className="flex-1">
                <div className="text-[11px] font-semibold text-[#8e8e96] mb-3.5">Featured on</div>
                <div className="grid grid-cols-1 gap-2">
                  {FEATURED_LOGOS.map((logo) => (
                    <div key={logo.name} className="flex items-center gap-2.5 px-3.5 py-[9px] rounded-xl bg-[rgba(255,255,255,0.65)] border border-[rgba(28,28,32,0.06)]">
                      <MarqueeLogo src={logo.src} name={logo.name} scale={logo.scale} className="h-5 lg:h-5" />
                    </div>
                  ))}
                  <div className="flex items-center justify-center px-3.5 py-[9px] rounded-xl bg-[rgba(139,92,246,0.09)] border border-[rgba(139,92,246,0.2)] text-[#6d28d9] text-[13px] font-semibold whitespace-nowrap">
                    + 28 more
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </header>
  );
}